import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types';
import { Button, Input } from '../components';
import { authService } from '../services/auth';
import { Colors, Fonts, Typography } from '../constants';
import { useResponsive } from '../utils/responsive';
import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'ForgotPassword'>;

export const ForgotPasswordScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const dialog = useDialog();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const isValidEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (emailError) setEmailError('');
  };

  const handleEmailBlur = () => {
    if (!email.trim()) {
      setEmailError(t('common.validation.emailRequired'));
    } else if (!isValidEmail(email.trim())) {
      setEmailError(t('common.validation.emailInvalid'));
    }
  };

  const handleSubmit = async () => {
    setEmailError('');

    if (!email.trim()) {
      setEmailError(t('common.validation.emailRequired'));
      return;
    }
    if (!isValidEmail(email.trim())) {
      setEmailError(t('common.validation.emailInvalid'));
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(email.trim());
      setSent(true);
    } catch (error: any) {
      dialog.alert(
        t('screens.forgotPassword.failed'),
        error.message || t('common.errors.unknown'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.formWrapper}>
          <View style={styles.header}>
            <Text style={[styles.title, !isDesktop && styles.titleMobile]}>
              {t('screens.forgotPassword.title')}
            </Text>
            <Text style={styles.subtitle}>
              {sent
                ? t('screens.forgotPassword.successMessage')
                : t('screens.forgotPassword.subtitle')}
            </Text>
          </View>

          {!sent ? (
            <View style={styles.form}>
              <Input
                label={t('screens.login.email')}
                icon="mail-outline"
                value={email}
                onChangeText={handleEmailChange}
                onBlur={handleEmailBlur}
                placeholder={t('screens.forgotPassword.emailPlaceholder')}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                returnKeyType="go"
                onSubmitEditing={handleSubmit}
                error={emailError}
              />
              <Button
                title={t('screens.forgotPassword.sendResetLink')}
                onPress={handleSubmit}
                loading={loading}
                style={styles.submitButton}
              />
            </View>
          ) : null}

          <Button
            title={t('common.buttons.backToLogin')}
            onPress={() => navigation.navigate('Login')}
            variant="outline"
            style={styles.backButton}
          />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 24,
    alignItems: 'center',
  },
  formWrapper: {
    width: '100%',
    ...(Platform.OS === 'web' && {
      maxWidth: 500,
    }),
  },
  header: {
    alignItems: 'center',
    marginBottom: 36,
  },
  title: {
    ...Typography.title,
    color: Colors.textDark,
    textAlign: 'center',
    marginBottom: 6,
  },
  titleMobile: {
    ...Typography.displayMobile,
  },
  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 22,
    color: Colors.textMedium,
    textAlign: 'center',
    maxWidth: 380,
  },
  form: {
    width: '100%',
  },
  submitButton: {
    marginTop: 8,
  },
  backButton: {
    marginTop: 16,
  },
});
